import { useState, useEffect } from 'react'
import useApi from '../api'
import { toast } from 'react-toastify'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"
import SchedulingOptionList from './SchedulingOptionList'

interface CalendlyProps {
    integrationId?: string;
    modalVisible: boolean;
    setModalVisible: any;
    onComplete?: () => void;
}

interface EventType {
    uri: string;
    name: string;
    scheduling_url: string;
    duration?: number;
    description?: string;
}

export default function Calendly({ integrationId, modalVisible, setModalVisible, onComplete }: CalendlyProps) {
    const [eventTypes, setEventTypes] = useState<EventType[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
    const [loading, setLoading] = useState(false);
    const [fetchingEventTypes, setFetchingEventTypes] = useState(false);
    const { get, patch } = useApi();

    // Fetch event types when modal opens with integration ID
    useEffect(() => {
        if (modalVisible && integrationId && eventTypes.length === 0) {
            fetchEventTypes();
        }
    }, [modalVisible, integrationId]);

    const fetchEventTypes = async () => {
        if (!integrationId) return;
        
        setFetchingEventTypes(true);
        try {
            const { data } = await get(`/integrations/calendly/${integrationId}/event-types`);
            setEventTypes(data.event_types || []);
            
            if (data.event_types.length === 0) {
                toast.error('No active event types found in your Calendly account');
            }
        } catch (error: any) {
            console.error('Error fetching Calendly event types:', error);
            toast.error(error?.detail || 'Failed to fetch event types from Calendly');
            setModalVisible(false);
        } finally {
            setFetchingEventTypes(false);
        }
    };

    const handleConnect = async () => {
        if (!selectedEvent || !integrationId) {
            toast.error('Please select an event type');
            return;
        }

        setLoading(true);
        try {
            await patch(`/integrations/calendly/${integrationId}/event-type`, {
                event_type_uri: selectedEvent.uri,
                calendly_url: selectedEvent.scheduling_url
            });
            toast.success('Calendly event type configured successfully', { autoClose: 2000 });
            handleClose();
            if (onComplete) onComplete();
        } catch (error: any) {
            console.error('Error updating Calendly integration:', error);
            toast.error(error?.detail || 'Failed to update event type');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setModalVisible(false);
        setSelectedEvent(null);
        setEventTypes([]);
    };

    return (
        <Dialog open={modalVisible} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Select Calendly Event Type</DialogTitle>
                    <DialogDescription>
                        Choose which event type you'd like to use for scheduling meetings with visitors.
                    </DialogDescription>
                </DialogHeader>

                {fetchingEventTypes ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                        <span className="ml-2">Loading event types...</span>
                    </div>
                ) : (
                    <div className="py-2">
                        <SchedulingOptionList
                            options={eventTypes.map((event) => ({
                                id: event.uri,
                                title: event.name,
                                badge: event.duration ? `${event.duration} min` : undefined,
                                subtitle: event.description || event.scheduling_url,
                            }))}
                            selectedId={selectedEvent?.uri || null}
                            onSelect={(uri) => {
                                const event = eventTypes.find(e => e.uri === uri);
                                setSelectedEvent(event || null);
                            }}
                            searchPlaceholder="Search event types..."
                        />
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConnect}
                        disabled={!selectedEvent || loading || fetchingEventTypes}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Connecting...
                            </>
                        ) : (
                            'Connect'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
